/**
 * OfacPanel — cotejo OFAC/listas del asociado (cumplimiento IVE).
 *
 * Muestra el estado del cotejo, permite consultar/re-consultar (edge function
 * ofac-screen, que deja bitácora), y si hay coincidencia (revision_pendiente)
 * deja al analista liberar o rechazar (RPC ofac_liberar_asociado_admin).
 *
 * El estado 'revision_pendiente' bloquea la activación del asociado.
 */
import { useCallback, useEffect, useState } from 'react'
import { ShieldCheck, ShieldWarning, Shield, ArrowClockwise } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Hit = { name: string | null; score: number; uid: string | number | null; source: string | null; category: string | null }
type Screening = {
  created_at: string; resultado: string; score_max: number | null; total_hits: number
  fuentes: string[]; hits: Hit[]; origen: string; decision: string | null; decision_nota: string | null
} | null
type Estado = { ofac_estado: string; ultima_consulta_at: string | null; screening: Screening; error?: string }

const META: Record<string, { label: string; color: string; bg: string; Icon: typeof Shield }> = {
  no_consultado: { label: 'Sin consultar', color: '#6B7280', bg: 'rgba(107,114,128,.10)', Icon: Shield },
  sin_coincidencia: { label: 'Sin coincidencias', color: '#0F8A57', bg: 'rgba(15,138,87,.10)', Icon: ShieldCheck },
  revision_pendiente: { label: 'Posible coincidencia · revisión', color: '#C0392B', bg: 'rgba(192,57,43,.10)', Icon: ShieldWarning },
  liberado: { label: 'Liberado por analista', color: '#0F8A57', bg: 'rgba(15,138,87,.10)', Icon: ShieldCheck },
  rechazado: { label: 'Rechazado', color: '#C0392B', bg: 'rgba(192,57,43,.10)', Icon: ShieldWarning },
  error: { label: 'Error en consulta', color: '#B45309', bg: 'rgba(180,83,9,.10)', Icon: ShieldWarning },
}

function fecha(iso: string) {
  return new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function OfacPanel({ asociadoId, nombreCompleto }: { asociadoId: string; nombreCompleto: string }) {
  const [est, setEst] = useState<Estado | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    const { data } = await supabase.rpc('ofac_estado_asociado', { p_asociado_id: asociadoId })
    setEst((data as Estado) ?? null)
  }, [asociadoId])
  useEffect(() => { load() }, [load])

  const consultar = async () => {
    setBusy(true); setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('ofac-screen', {
        body: { asociado_id: asociadoId, origen: est?.ofac_estado === 'no_consultado' ? 'manual' : 'reconsulta' },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error === 'ofac_api_key_no_configurada'
        ? 'Falta configurar el secret OFAC_API_KEY en el edge function.' : data.error)
      await load()
    } catch (e) { setErr(e instanceof Error ? e.message : 'No pudimos consultar OFAC.') }
    finally { setBusy(false) }
  }

  const decidir = async (decision: 'liberado' | 'rechazado') => {
    const nota = window.prompt(decision === 'liberado'
      ? 'Nota de liberación (por qué no es la persona listada):'
      : 'Motivo del rechazo:') ?? ''
    if (decision === 'rechazado' && !nota.trim()) return
    setBusy(true); setErr(null)
    try {
      const { error } = await supabase.rpc('ofac_liberar_asociado_admin', {
        p_asociado_id: asociadoId, p_decision: decision, p_nota: nota.trim() || null,
      })
      if (error) throw error
      await load()
    } catch (e) { setErr(e instanceof Error ? e.message : 'No pudimos registrar la decisión.') }
    finally { setBusy(false) }
  }

  const estado = est?.ofac_estado ?? 'no_consultado'
  const m = META[estado] ?? META.no_consultado
  const scr = est?.screening
  const enRevision = estado === 'revision_pendiente'

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: m.bg }}>
        <m.Icon size={22} weight="duotone" color={m.color} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: m.color }}>Cotejo OFAC / listas · {m.label}</div>
          <div style={{ fontSize: 11, color: 'var(--n-600)', marginTop: 2 }}>
            {est?.ultima_consulta_at
              ? `Última consulta ${fecha(est.ultima_consulta_at)} · SDN · NONSDN · DPL · UN`
              : 'Todavía no se ha cotejado contra listas.'}
          </div>
        </div>
        <button className="btn btn-ghost" disabled={busy} onClick={consultar}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ArrowClockwise size={14} weight="bold" />
          {busy ? 'Consultando…' : est?.ultima_consulta_at ? 'Re-consultar' : 'Consultar OFAC'}
        </button>
      </div>

      {scr && scr.total_hits > 0 ? (
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--n-500)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
            {scr.total_hits} coincidencia{scr.total_hits === 1 ? '' : 's'} · score máx {scr.score_max}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {scr.hits.slice(0, 5).map((h, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 10px', background: 'var(--n-50)', borderRadius: 8 }}>
                <span style={{ fontWeight: 500 }}>{h.name ?? '—'}</span>
                <span style={{ color: 'var(--n-500)' }}>{h.source ?? ''} · {h.category ?? ''} · <b style={{ color: 'var(--n-900)' }}>{h.score}</b></span>
              </div>
            ))}
          </div>
          {enRevision ? (
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn btn-primary" disabled={busy} onClick={() => decidir('liberado')}>Liberar (no es la persona)</button>
              <button className="btn btn-ghost" disabled={busy} onClick={() => decidir('rechazado')} style={{ color: '#C0392B' }}>Rechazar</button>
            </div>
          ) : scr.decision ? (
            <div style={{ fontSize: 11, color: 'var(--n-600)', marginTop: 10 }}>
              Decisión: <b>{scr.decision}</b>{scr.decision_nota ? ` · "${scr.decision_nota}"` : ''}
            </div>
          ) : null}
        </div>
      ) : null}

      {err ? <div style={{ padding: '10px 16px', fontSize: 12, color: '#C0392B', borderTop: '1px solid var(--border)' }}>{err}</div> : null}
      <div style={{ padding: '8px 16px', fontSize: 10, color: 'var(--n-400)', borderTop: '1px solid var(--border)' }}>
        Bitácora de cumplimiento IVE · consultado contra {nombreCompleto}
      </div>
    </div>
  )
}
